import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { FileText, Download, Eye, Edit, Trash2, Plus, X } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { documentService } from '../../services/documentService';

const PoliciesDocuments = () => {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editingDoc, setEditingDoc] = useState(null);
  const [viewingDoc, setViewingDoc] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    document_type: 'policy',
    visibility: 'internal',
    file: null
  });

  const { data: documentsData, isLoading } = useQuery('policies-documents', async () => {
    const response = await documentService.getDocuments();
    return response.data?.data || response.data;
  });

  const documents = documentsData?.documents || [];

  const createMutation = useMutation(
    async (data) => {
      const formDataObj = new FormData();
      formDataObj.append('title', data.title);
      formDataObj.append('description', data.description || '');
      formDataObj.append('document_type', data.document_type);
      formDataObj.append('visibility', data.visibility);
      if (data.file) {
        formDataObj.append('file', data.file);
      }
      return documentService.createDocument(formDataObj);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('policies-documents');
        toast.success('Document created successfully');
        resetForm();
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to create document');
      }
    }
  );

  const updateMutation = useMutation(
    ({ id, ...data }) => documentService.updateDocument(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('policies-documents');
        toast.success('Document updated successfully');
        resetForm();
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to update document');
      }
    }
  );

  const deleteMutation = useMutation(
    (id) => documentService.deleteDocument(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('policies-documents');
        toast.success('Document deleted successfully');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to delete document');
      }
    }
  );

  const resetForm = () => {
    setFormData({ title: '', description: '', document_type: 'policy', visibility: 'internal', file: null });
    setShowForm(false);
    setEditingDoc(null);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (editingDoc) {
      updateMutation.mutate({ id: editingDoc.id, title: formData.title, description: formData.description, document_type: formData.document_type, visibility: formData.visibility });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleEdit = (doc) => {
    setEditingDoc(doc);
    setFormData({
      title: doc.title,
      description: doc.description || '',
      document_type: doc.document_type || 'policy',
      visibility: doc.visibility || 'internal',
      file: null
    });
    setShowForm(true);
  };

  const handleDelete = (doc) => {
    if (window.confirm(`Are you sure you want to delete "${doc.title}"?`)) {
      deleteMutation.mutate(doc.id);
    }
  };

  const getTypeColor = (type) => {
    const colors = {
      policy: 'bg-blue-100 text-blue-800',
      procedure: 'bg-green-100 text-green-800',
      manual: 'bg-purple-100 text-purple-800',
      form: 'bg-yellow-100 text-yellow-800',
      contract: 'bg-red-100 text-red-800'
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  if (isLoading) return <div className="flex justify-center items-center h-64">Loading...</div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Policies & Documents</h2>
          <p className="text-gray-600 mt-1">Manage company policies, procedures, and documents</p>
        </div>
        <button
          onClick={() => { resetForm(); setShowForm(true); }}
          className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
        >
          <Plus className="h-4 w-4" />
          <span>Add Document</span>
        </button>
      </div>

      {showForm && (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium">{editingDoc ? 'Edit Document' : 'New Document'}</h3>
            <button onClick={resetForm} className="text-gray-400 hover:text-gray-600">
              <X className="h-5 w-5" />
            </button>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Title *</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-blue-500 focus:border-blue-500"
                rows={3}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Type</label>
                <select
                  value={formData.document_type}
                  onChange={(e) => setFormData({ ...formData, document_type: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="policy">Policy</option>
                  <option value="procedure">Procedure</option>
                  <option value="manual">Manual</option>
                  <option value="form">Form</option>
                  <option value="contract">Contract</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Visibility</label>
                <select
                  value={formData.visibility}
                  onChange={(e) => setFormData({ ...formData, visibility: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="public">Public</option>
                  <option value="internal">Internal</option>
                  <option value="department">Department Only</option>
                  <option value="private">Private</option>
                </select>
              </div>
            </div>
            {!editingDoc && (
              <div>
                <label className="block text-sm font-medium mb-1">File</label>
                <input
                  type="file"
                  onChange={(e) => setFormData({ ...formData, file: e.target.files[0] })}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
            )}
            <div className="flex space-x-2 pt-2">
              <button 
                type="submit" 
                disabled={createMutation.isLoading || updateMutation.isLoading}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {createMutation.isLoading || updateMutation.isLoading ? 'Saving...' : (editingDoc ? 'Update' : 'Create')}
              </button>
              <button type="button" onClick={resetForm} className="px-4 py-2 border rounded-lg hover:bg-gray-50">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {documents.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No documents yet</h3>
          <p className="text-gray-500">Get started by adding your first policy or document.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {documents.map((doc) => (
            <div key={doc.id} className="bg-white rounded-lg shadow p-4 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between mb-3">
                <FileText className="h-8 w-8 text-blue-600" />
                <span className={`text-xs px-2 py-1 rounded-full ${getTypeColor(doc.document_type)}`}>
                  {doc.document_type}
                </span>
              </div>
              <h3 className="font-medium text-gray-900 mb-1">{doc.title}</h3>
              {doc.description && <p className="text-sm text-gray-600 mb-3 line-clamp-2">{doc.description}</p>}
              <div className="text-xs text-gray-400 mb-3">
                {doc.CreatedBy?.User && `By ${doc.CreatedBy.User.first_name} ${doc.CreatedBy.User.last_name}`}
                {doc.created_at && ` • ${new Date(doc.created_at).toLocaleDateString()}`}
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => setViewingDoc(doc)}
                  className="flex-1 px-3 py-1.5 text-sm border rounded hover:bg-gray-50 flex items-center justify-center"
                >
                  <Eye className="h-4 w-4 mr-1" />
                  View
                </button>
                {doc.cdn_url && (
                  <button
                    onClick={() => window.open(doc.cdn_url, '_blank')}
                    className="flex-1 px-3 py-1.5 text-sm border rounded hover:bg-gray-50 flex items-center justify-center"
                  >
                    <Download className="h-4 w-4 mr-1" />
                    Download
                  </button>
                )}
                <button
                  onClick={() => handleEdit(doc)}
                  className="px-3 py-1.5 text-sm border rounded hover:bg-gray-50"
                >
                  <Edit className="h-4 w-4" />
                </button>
                <button
                  onClick={() => handleDelete(doc)}
                  className="px-3 py-1.5 text-sm border rounded hover:bg-red-50 text-red-600"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* View Modal */}
      {viewingDoc && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b flex justify-between items-start">
              <div>
                <h3 className="text-xl font-bold text-gray-900">{viewingDoc.title}</h3>
                <span className={`text-xs px-2 py-1 rounded-full ${getTypeColor(viewingDoc.document_type)}`}>
                  {viewingDoc.document_type}
                </span>
              </div>
              <button onClick={() => setViewingDoc(null)} className="text-gray-400 hover:text-gray-600">
                <X className="h-6 w-6" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              {viewingDoc.description && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <p className="text-gray-600">{viewingDoc.description}</p>
                </div>
              )}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><span className="text-gray-500">Visibility:</span> {viewingDoc.visibility}</div>
                <div><span className="text-gray-500">Status:</span> {viewingDoc.status}</div>
                <div><span className="text-gray-500">Created:</span> {viewingDoc.created_at ? new Date(viewingDoc.created_at).toLocaleDateString() : '-'}</div>
                <div><span className="text-gray-500">Version:</span> {viewingDoc.version || '1.0'}</div>
              </div>
              {viewingDoc.content && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Content</label>
                  <div className="p-4 bg-gray-50 rounded-lg whitespace-pre-wrap">{viewingDoc.content}</div>
                </div>
              )}
              {viewingDoc.cdn_url && (
                <div className="pt-4">
                  <a
                    href={viewingDoc.cdn_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Download File
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PoliciesDocuments;
